'use client';

import type { RuleAnchorOption } from './ruleAnchor';

export interface RuleCreationValue {
  createRule: boolean;
  applyToMerchant: boolean;
}

/**
 * The rule-creation checkbox pair used wherever categorizing can mint a
 * merchant rule (review queue rows, the Categorizar drawer). The interlock is
 * owned here: applying to prior lançamentos implies the rule; unchecking the
 * rule clears the apply.
 *
 * Everything reads off the ANCHOR, never the transaction: the copy names what the rule
 * will cover, and the prior count is the one that anchor reaches. Both therefore change
 * when the user flips the anchor — a static count beside a live selector would describe
 * a different set than the write touches.
 */
export function RuleCreationFields({
  anchor,
  categoryName,
  value,
  onChange,
  disabled = false,
  showExplanation = false,
  className = '',
}: {
  anchor: RuleAnchorOption;
  categoryName: string;
  value: RuleCreationValue;
  onChange: (next: RuleCreationValue) => void;
  disabled?: boolean;
  /** Renders the "novos lançamentos entram direto…" note while the rule is on. */
  showExplanation?: boolean;
  className?: string;
}) {
  const isMerchant = anchor.kind === 'merchant';
  return (
    <div className={`flex flex-col gap-1.5 text-xs text-text-secondary ${className}`}>
      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          className="mt-0.5 accent-brand-primary"
          checked={value.createRule}
          disabled={disabled}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? { ...value, createRule: true }
                : { createRule: false, applyToMerchant: false },
            )
          }
        />
        {/* "tudo de" is what carries the widened scope — a merchant rule covers every
            descriptor variant of the identity, not the one text on screen. */}
        <span>
          Sempre categorizar{' '}
          {isMerchant ? (
            <>
              tudo de <b className="text-text-primary">{anchor.label}</b>
            </>
          ) : (
            <b className="text-text-primary">“{anchor.label}”</b>
          )}{' '}
          como <b className="text-text-primary">{categoryName}</b>
        </span>
      </label>
      {anchor.priorCount > 0 && (
        <label className="flex cursor-pointer items-start gap-2">
          <input
            type="checkbox"
            className="mt-0.5 accent-brand-primary"
            checked={value.applyToMerchant}
            disabled={disabled}
            onChange={(e) =>
              onChange(
                e.target.checked
                  ? { createRule: true, applyToMerchant: true }
                  : { ...value, applyToMerchant: false },
              )
            }
          />
          <span>
            Aplicar também{' '}
            {anchor.priorCount === 1
              ? 'ao lançamento anterior'
              : `aos ${anchor.priorCount} lançamentos anteriores`}
          </span>
        </label>
      )}
      {showExplanation && value.createRule && (
        <p className="pl-6 text-2xs text-success-text">
          Novos lançamentos de {anchor.label}
          {isMerchant ? ', em qualquer descrição,' : ''} entram direto como {categoryName}, sem
          passar pela fila. Dá para remover depois em “Regras desta categoria”.
        </p>
      )}
    </div>
  );
}
